// OptionGroup.jsx
import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  OptionGroupRoot,
  OptionsList,
  OptionButton,
} from "./OptionGroup.styles";
import { ColorWheel } from "../ColorWheel";
import { CustomSlider } from "../CustomSlider";

export const OptionGroup = ({
  title,
  items,
  clickOnAction,
  onHoverChange,
  onSelectOption,
  selectedMap, // context map
  subCategoryName, // current subcategory label
  cameras,
}) => {
  const theme = useTheme();

  const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // If context has a value for this group, use context to decide "active".
  // has NO selection in context yet (e.g., before initialization finishes).
  // Float-RGBA helper: "r=0.102,g=0.435,b=0.290,a=1"
  const isFloatRgbaString = (val) => {
    if (typeof val !== "string") return false;
    return /^r=\d*\.?\d+,\s*g=\d*\.?\d+,\s*b=\d*\.?\d+,\s*a=\d*\.?\d+$/i.test(
      val
    );
  };

  // Use only context to determine active state
  const computeIsActive = (opt, selectedMap) => {
    const descriptor = opt?.action?.descriptor;
    if (!descriptor) return false;

    const key = Object.keys(descriptor)[0];
    const selectedVal = selectedMap?.[key]?.descriptor?.[key];

    if (opt?.customRGB) {
      // active if the selected value equals the descriptor (e.g. "ipf")
      // OR if the selected value is a float RGBA string
      return selectedVal === descriptor[key] || isFloatRgbaString(selectedVal);
    }

    // normal (non-RGB) options: strict equality
    return selectedVal === descriptor[key];
  };
  return (
    <OptionGroupRoot>
      <Box
        sx={{
          padding: "0.75rem 0",
          [theme.breakpoints.up("xl")]: {
            padding: "0.5rem 0",
          },
        }}
      >
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              color: theme?.palette?.companyColors?.secondary,
              borderBottom: `1px solid ${theme?.palette?.companyColors?.secondary}`,
              margin: "1rem 0",
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      <OptionsList length={items?.length ?? 0}>
        {(items ?? []).map((opt, idx) => {
          const descriptor = opt?.action?.descriptor;
          const isActive = computeIsActive(opt, selectedMap);

          const disabled = false;

          const handleClick = () => {
            if (disabled) return; // block if disabled
            clickOnAction(descriptor);
            onSelectOption?.({
              name: opt?.name,
              image: opt?.image,
              price: opt?.price,
              descriptor,
              inSummary: opt?.inSummary,
              subCategory: subCategoryName || title || "",
            });
          };

          const {
            type,
            min,
            max,
            step,
            marks,
            defaultValue,
            unitBefore,
            unitAfter,
            valueLabelFormatter,
          } = opt?.action;

          const groupKey = descriptor && Object.keys(descriptor)[0];
          // get the current color (RGB string) for this group from context
          const currentColor = opt?.customRGB
            ? selectedMap?.[groupKey]?.color ?? "0.537,0.737,0.651"
            : undefined;

          // when the color changes, send RGB to UE, but keep original descriptor in context
          const handleColorChange = (floatStr) => {
            if (disabled) return;
            const rgbDescriptor = { [groupKey]: floatStr }; // what UE needs
            clickOnAction(rgbDescriptor);

            onSelectOption?.({
              name: opt?.name,
              image: opt?.image,
              price: opt?.price,
              descriptor, // keep original descriptor for your selection model
              subCategory: subCategoryName || title || "",
              inSummary: opt?.inSummary,
              color: floatStr, // persist RGB for ColorWheel position & summary
            });
          };

          // current value from context (falls back to action.defaultValue)
          const rawValue =
            selectedMap?.[groupKey]?.descriptor?.[groupKey] || defaultValue;
          const currentValue = parseFloat(rawValue);

          const finalValue = isNaN(currentValue) ? defaultValue : currentValue;

          const handleSliderChange = (event, newValue) => {
            // send to UE
            clickOnAction({ [groupKey]: newValue });
            // store in context so UI stays controlled
            onSelectOption?.({
              name: opt?.name,
              image: opt?.image,
              price: opt?.price,
              descriptor: { [groupKey]: newValue }, // persist the numeric value
              inSummary: opt?.inSummary,
              subCategory: subCategoryName || title || "",
            });
          };

          if (type === "slider") {
            return (
              <Box sx={{ width: "200px", marginRight: "40px" }}>
                <CustomSlider
                  key={`${opt?.name}${idx}`}
                  aria-label={opt?.name}
                  value={finalValue}
                  step={step}
                  marks={marks}
                  min={min}
                  max={max}
                  unitBefore={unitBefore}
                  unitAfter={unitAfter}
                  valueLabelFormatter={valueLabelFormatter}
                  sx={{
                    width: "100%",
                  }}
                  onChange={handleSliderChange}
                />
              </Box>
            );
          } else {
            const canClick = !disabled && !isActive;
            if (opt?.baseOption) return undefined;
            return (
              <>
                <OptionButton
                  key={`${opt?.name}${idx}`}
                  title={opt?.name}
                  active={isActive && !disabled}
                  disabled={!canClick}
                  cameras={cameras}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    if (!canClick) return;

                    handleClick();
                  }}
                  onMouseEnter={() => onHoverChange?.(opt?.name)}
                  onMouseLeave={() => onHoverChange?.(null)}
                  sx={{
                    opacity: 1,
                    cursor: !canClick ? "not-allowed" : "pointer",
                    pointerEvents: !canClick ? "none" : "auto",
                  }}
                >
                  <Box component="img" src={opt?.image} alt={opt?.name} />
                  {opt?.price > 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: -2,
                        padding: "3px 0.75rem 1px",
                        fontWeight: 400,
                        color: theme?.palette?.companyColors?.neutral,
                        background: theme?.palette?.companyColors?.secondary,
                        borderRadius: "2px 10px 10px 2px",
                        boxShadow: "0px 0px 5px rgba(0,0,0,0.5)",
                      }}
                    >
                      {currency.format(opt?.price)}
                    </Typography>
                  )}
                  <Typography
                    variant="body1"
                    sx={{
                      textTransform: "none",
                      position: "absolute",
                      bottom: "-2rem",
                      fontWeight: 400,
                      color: cameras
                        ? theme?.palette?.companyColors?.neutral
                        : theme?.palette?.companyColors?.darkGrey,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt?.name}
                  </Typography>
                </OptionButton>
                {opt?.customRGB && isActive && (
                  <ColorWheel
                    onCurrentColorChange={handleColorChange}
                    currentColor={currentColor}
                  />
                )}
              </>
            );
          }
        })}
      </OptionsList>
    </OptionGroupRoot>
  );
};

export default OptionGroup;
